import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getCountFromServer, query, where } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { Compass, LayoutDashboard, Users, Mail, Zap, FileSignature, BarChart3, PlugZap, Settings, FolderOpen, LayoutTemplate } from 'lucide-react';
import { db } from '../firebase';
import { integrationService } from '../services/integrationService';
import UpgradeModal from './UpgradeModal';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const { currentWorkspace, workspaces, switchWorkspace, createWorkspace, isCurrentWorkspaceLocked } = useWorkspace();
  const { setIsSidebarVisible } = useSidebar();
  const { showError } = useNotification();
  const { theme } = useTheme();
  const isLightTheme = theme === 'light';
  const sidebarBorderClass = isLightTheme ? 'border-black/10' : 'border-white/10';
  const navActiveClass = isLightTheme ? 'bg-[rgba(15,23,42,0.12)] text-slate-900 font-[400]' : 'bg-white/20 text-white font-[400]';
  const navInactiveClass = isLightTheme ? 'hover:bg-black/5 text-slate-600' : 'hover:bg-white/10 text-white';
  const submenuActiveClass = isLightTheme ? 'bg-[rgba(15,23,42,0.10)] text-slate-900 font-[400]' : 'bg-white/20 text-white font-[400]';
  const submenuInactiveClass = isLightTheme ? 'text-slate-600 hover:bg-black/3' : 'text-white hover:bg-white/10';
  const closeButtonClass = isLightTheme ? 'text-slate-600 hover:bg-black/5 active:bg-black/10' : 'text-white hover:bg-white/10 active:bg-white/20';
  const secondaryTextColor = isLightTheme ? '#4B5563' : '#F5F5F5';

  // State for inline workspace creation
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // State for content management modal
  const [showContentModal, setShowContentModal] = useState(false);

  // State for Contacts menu expansion
  const [isContactsExpanded, setIsContactsExpanded] = useState(false);

  // State for Settings menu expansion
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [isShopifyExpanded, setIsShopifyExpanded] = useState(false);
  const [isShopifyConnected, setIsShopifyConnected] = useState(false);
  const [shopifyStoreName, setShopifyStoreName] = useState<string>('');
  const [shopifyStoreDomain, setShopifyStoreDomain] = useState<string>('');
  const [shopifyIntegrationId, setShopifyIntegrationId] = useState<string>('');
  const [shopifyLastSyncAt, setShopifyLastSyncAt] = useState<Date | null>(null);
  const [shopifyStats, setShopifyStats] = useState<{
    customers: number;
    orders: number;
    products: number;
  }>({ customers: 0, orders: 0, products: 0 });
  const [isShopifyStatsLoading, setIsShopifyStatsLoading] = useState(false);
  const [isShopifySyncing, setIsShopifySyncing] = useState(false);

  // Upgrade Modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [activeLockTooltip, setActiveLockTooltip] = useState<string | null>(null);
  const [shopifyMenuPosition, setShopifyMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const shopifyButtonRef = useRef<HTMLButtonElement | null>(null);
  const shopifyMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const checkShopifyConnection = async () => {
      if (!currentUser?.uid || !currentWorkspace?.id) {
        setIsShopifyConnected(false);
        setIsShopifyExpanded(false);
        setShopifyStoreName('');
        setShopifyStoreDomain('');
        setShopifyIntegrationId('');
        setShopifyLastSyncAt(null);
        setShopifyStats({ customers: 0, orders: 0, products: 0 });
        return;
      }
      try {
        const integrations = await integrationService.getUserIntegrations(currentUser.uid, currentWorkspace.id);
        const shopifyIntegration = integrations.find(
          (i) => i.slug === 'shopify' && i.status === 'connected'
        );
        const connected = !!shopifyIntegration;
        setIsShopifyConnected(connected);
        setShopifyIntegrationId(shopifyIntegration?.id || '');
        setShopifyLastSyncAt(shopifyIntegration?.lastSync || shopifyIntegration?.updatedAt || null);
        const rawDomain = (shopifyIntegration?.shopDomain || '').trim();
        const normalizedDomain = rawDomain
          .replace(/^https?:\/\//i, '')
          .replace(/\/.*$/, '');
        const displayName = rawDomain
          ? rawDomain.replace(/\.myshopify\.com$/i, '').replace(/^https?:\/\//i, '')
          : '';
        setShopifyStoreName(displayName);
        setShopifyStoreDomain(normalizedDomain);
        if (!connected) {
          setIsShopifyExpanded(false);
          setShopifyStoreName('');
          setShopifyStoreDomain('');
          setShopifyIntegrationId('');
          setShopifyLastSyncAt(null);
          setShopifyStats({ customers: 0, orders: 0, products: 0 });
        }
      } catch (error) {
        console.error('Error checking Shopify connection for sidebar:', error);
        setIsShopifyConnected(false);
        setIsShopifyExpanded(false);
        setShopifyStoreName('');
        setShopifyStoreDomain('');
        setShopifyIntegrationId('');
        setShopifyLastSyncAt(null);
        setShopifyStats({ customers: 0, orders: 0, products: 0 });
      }
    };

    checkShopifyConnection();
  }, [currentUser?.uid, currentWorkspace?.id]);

  const loadShopifyStoreGlance = async () => {
    if (!currentWorkspace?.id || !currentUser?.uid || !isShopifyConnected) return;
    try {
      setIsShopifyStatsLoading(true);
      const contactsQuery = query(
        collection(db, 'contacts'),
        where('workspaceId', '==', currentWorkspace.id),
        where('userId', '==', currentUser.uid),
        where('source', '==', 'shopify')
      );
      const ordersQuery = query(
        collection(db, 'ecommerce_events'),
        where('workspaceId', '==', currentWorkspace.id),
        where('userId', '==', currentUser.uid),
        where('eventType', '==', 'placed_order')
      );
      const productsQuery = query(
        collection(db, 'shopifyStoreProducts'),
        where('workspaceId', '==', currentWorkspace.id),
        where('userId', '==', currentUser.uid)
      );

      const [customersCount, ordersCount, productsCount] = await Promise.all([
        getCountFromServer(contactsQuery),
        getCountFromServer(ordersQuery),
        getCountFromServer(productsQuery),
      ]);

      setShopifyStats({
        customers: customersCount.data().count,
        orders: ordersCount.data().count,
        products: productsCount.data().count,
      });
    } catch (error) {
      console.error('Error loading Shopify store glance:', error);
    } finally {
      setIsShopifyStatsLoading(false);
    }
  };

  useEffect(() => {
    if (isShopifyExpanded) {
      loadShopifyStoreGlance();
    }
  }, [isShopifyExpanded, currentWorkspace?.id, currentUser?.uid, isShopifyConnected]);

  const formatRelativeSyncTime = (date: Date | null) => {
    if (!date) return 'Not synced yet';
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 60 * 1000) return 'Just now';
    const minutes = Math.floor(diffMs / (60 * 1000));
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleShopifySync = async () => {
    if (isShopifySyncing) return;
    if (!shopifyIntegrationId || !currentUser?.uid || !currentWorkspace?.id) {
      showError('Shopify connection is not ready yet. Please reopen the panel and try again.');
      return;
    }
    try {
      setIsShopifySyncing(true);
      // Optimistic feedback so the user immediately sees sync activity.
      setShopifyLastSyncAt(new Date());
      await integrationService.syncData(shopifyIntegrationId, { force: true });
      const integrations = await integrationService.getUserIntegrations(currentUser.uid, currentWorkspace.id);
      const refreshedShopify = integrations.find(
        (i) => i.slug === 'shopify' && i.status === 'connected'
      );
      if (refreshedShopify) {
        setShopifyIntegrationId(refreshedShopify.id);
        setShopifyLastSyncAt(refreshedShopify.lastSync || refreshedShopify.updatedAt || new Date());
        const refreshedDomain = (refreshedShopify.shopDomain || '')
          .trim()
          .replace(/^https?:\/\//i, '')
          .replace(/\/.*$/, '');
        setShopifyStoreDomain(refreshedDomain);
      }
      await loadShopifyStoreGlance();
    } catch (error) {
      console.error('Error syncing Shopify store:', error);
      showError('Shopify sync failed. Please try again.');
    } finally {
      setIsShopifySyncing(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = () => setActiveLockTooltip(null);
    if (activeLockTooltip) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeLockTooltip]);



  // Contacts submenu items
  const contactsSubmenuItems = [
    { name: 'All contacts', path: '/contacts' },
    { name: 'Fields', path: '/contacts/fields' },
    { name: 'Segments', path: '/contacts/segments' },
    { name: 'Tags', path: '/contacts/tags' },
    { name: 'Import', path: '/contacts/import' },
    { name: 'Export', path: '/contacts/export' },
    { name: 'Contact Hygiene', path: '/contacts/hygiene' },
  ];



  // Settings submenu items
  const settingsSubmenuItems = [
    { name: 'Profile', path: '/account/profile' },
    { name: 'Account Settings', path: '/account/settings' },
    { name: 'Billing', path: '/account/billing' },
  ];

  const shopifySubmenuItems = currentWorkspace ? [
    { name: 'Customers', path: `/workspace/${currentWorkspace.id}/shopify/customers` },
    { name: 'Orders', path: `/workspace/${currentWorkspace.id}/shopify/orders` },
    { name: 'Products', path: `/workspace/${currentWorkspace.id}/shopify-products` },
    { name: 'Abandoned Carts', path: `/workspace/${currentWorkspace.id}/shopify/abandoned-carts` },
    { name: 'Integration Settings', path: `/workspace/${currentWorkspace.id}/integration/setup/shopify` },
  ] : [];

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };
  const isShopifySectionActive = shopifySubmenuItems.some((item) => isActiveRoute(item.path));
  const shouldEnableSidebarScroll = isSettingsExpanded;


  const handleCreateWorkspace = () => {
    setIsCreatingWorkspace(true);
    setNewWorkspaceName('');
  };

  const handleCancelCreate = () => {
    setIsCreatingWorkspace(false);
    setNewWorkspaceName('');
  };

  const handleSubmitCreate = async () => {
    if (newWorkspaceName.trim() && !isCreating) {
      setIsCreating(true);
      try {
        const workspaceData = {
          name: newWorkspaceName.trim(),
          description: '',
          primaryColor: '#3B82F6', // Default blue color
          secondaryColor: '#EFF6FF', // Light blue background
          timezone: 'UTC',
          currency: 'USD',
          language: 'en'
        };

        // Pre-check workspace limits
        const { planService } = await import('../services/planService');
        if (currentUser) {
          const limitCheck = await planService.canCreateWorkspace(currentUser.uid);
          if (!limitCheck.allowed) {
            // Replace alert with Upgrade Modal
            setShowUpgradeModal(true);
            setIsCreatingWorkspace(false);
            setNewWorkspaceName('');
            return;
          }
        }

        const newWorkspaceId = await createWorkspace(workspaceData);
        console.log('Workspace created successfully:', newWorkspaceId);

        // Switch to the newly created workspace
        await switchWorkspace(newWorkspaceId);

        // Close the input and reset
        setIsCreatingWorkspace(false);
        setNewWorkspaceName('');
      } catch (error) {
        console.error('Error creating workspace:', error);
        // You might want to show an error message to the user here
      } finally {
        setIsCreating(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmitCreate();
    } else if (e.key === 'Escape') {
      handleCancelCreate();
    }
  };

  const resolveWorkspacePath = (path: string) => {
    if (path === '/analytics' || path === '/reports') {
      if (currentWorkspace?.id) {
        return `/workspace/${currentWorkspace.id}${path}`;
      }
      if (workspaces.length > 0) {
        return `/workspace/${workspaces[0].id}${path}`;
      }
      return path;
    }
    return currentWorkspace ? `/workspace/${currentWorkspace.id}${path}` : path;
  };

  // Helper function to navigate and close sidebar on mobile
  const handleNavigation = (path: string) => {
    navigate(path);
    // Close sidebar on mobile (screen width < 1024px)
    if (window.innerWidth < 1024) {
      setIsSidebarVisible(false);
    }
  };

  const updateShopifyFlyoutPosition = () => {
    const buttonEl = shopifyButtonRef.current;
    if (!buttonEl) return;
    const rect = buttonEl.getBoundingClientRect();
    const menuHeight = shopifyMenuRef.current?.offsetHeight || 0;
    const viewportHeight = window.innerHeight;
    const padding = 8;
    // Prefer aligning the menu's bottom with the button's bottom near the footer.
    const preferredTop = rect.bottom - menuHeight;
    const clampedTop = Math.max(
      padding,
      Math.min(preferredTop, viewportHeight - menuHeight - padding)
    );

    setShopifyMenuPosition({
      top: clampedTop,
      left: rect.right + 8,
    });
  };

  useEffect(() => {
    if (!isShopifyExpanded) return;

    updateShopifyFlyoutPosition();
    const onWindowChange = () => updateShopifyFlyoutPosition();
    const onGlobalPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const clickedButton = !!(target && shopifyButtonRef.current?.contains(target));
      const clickedMenu = !!(target && shopifyMenuRef.current?.contains(target));
      if (!clickedButton && !clickedMenu) {
        setIsShopifyExpanded(false);
      }
    };
    window.addEventListener('resize', onWindowChange);
    window.addEventListener('scroll', onWindowChange, true);
    window.addEventListener('mousedown', onGlobalPointerDown);
    return () => {
      window.removeEventListener('resize', onWindowChange);
      window.removeEventListener('scroll', onWindowChange, true);
      window.removeEventListener('mousedown', onGlobalPointerDown);
    };
  }, [isShopifyExpanded]);

  useEffect(() => {
    if (isShopifyExpanded) {
      // Recompute once menu content has rendered/measured.
      updateShopifyFlyoutPosition();
    }
  }, [isShopifyExpanded, shopifySubmenuItems.length]);

  interface WorkspaceMenuItem {
    name: string;
    path: string;
    icon: React.ReactNode;
    badge?: string;
  }

  // Workspace menu items (collapsible under each workspace)
  const workspaceMenuItems: WorkspaceMenuItem[] = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: <LayoutDashboard className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-4 lg:h-4" strokeWidth={1.8} />
    },
    {
      name: 'Contacts',
      path: '/contacts',
      icon: <Users className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-4 lg:h-4" strokeWidth={1.8} />
    },
    {
      name: 'Campaigns',
      path: '/campaigns',
      icon: <Mail className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-4 lg:h-4" strokeWidth={1.8} />
    },
    {
      name: 'Automations',
      path: '/automations',
      icon: <Zap className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-4 lg:h-4" strokeWidth={1.8} />
    },
    {
      name: 'Landing Page',
      path: '/landing-pages',
      icon: (
        <svg className="w-3.5 h-3.5 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      name: 'Signup Form',
      path: '/signup-forms',
      icon: <FileSignature className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-4 lg:h-4" strokeWidth={1.8} />
    },
    {
      name: 'Insights',
      path: '/analytics',
      icon: <BarChart3 className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-4 lg:h-4" strokeWidth={1.8} />
    },
    {
      name: 'Integration',
      path: '/integration',
      icon: <PlugZap className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-4 lg:h-4" strokeWidth={1.8} />
    },
    {
      name: 'Workspace settings',
      path: '/settings',
      icon: <Settings className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-4 lg:h-4" strokeWidth={1.8} />
    }
  ];

  const templatesPath = '/templates';


  return (
    <div className={`h-screen flex flex-col font-['Arial'] w-full lg:w-[260px] text-sm sm:text-sm lg:text-[13px] ${sidebarBorderClass}`} style={{ backgroundColor: theme === 'dark' ? '#111113' : '#E5E5E5' }}>
      {/* Header — always dark to match the app header */}
      <header className={`px-3 sm:px-4 lg:px-4 py-2.5 sm:py-3 lg:py-4 h-[52px] sm:h-[60px] lg:h-[73px] flex items-center justify-between lg:justify-start`} style={{ backgroundColor: '#111113' }}>
        {/* Logo - Hidden on mobile, visible on desktop */}
        <button
          onClick={() => {
            if (currentWorkspace?.id) {
              navigate(`/workspace/${currentWorkspace.id}/dashboard`);
            } else if (workspaces.length > 0) {
              navigate(`/workspace/${workspaces[0].id}/dashboard`);
            } else {
              navigate('/dashboard');
            }
          }}
          className="hidden lg:block lg:ml-3 hover:opacity-80 transition-opacity duration-200"
        >
          <img
            src={"/Scubamal logo for black bg.png"}
            alt="Scubamail Logo"
            width={120}
            height={24}
            className="h-6 w-auto object-contain"
          />
        </button>
        {/* Close button - mobile only */}
        <button
          onClick={() => setIsSidebarVisible(false)}
          className={`lg:hidden p-2.5 sm:p-2 rounded-lg transition-colors duration-200 ml-auto touch-manipulation ${closeButtonClass}`}
          aria-label="Close sidebar"
        >
          <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      {/* Workspace Menu Items Section */}
      <div
        className={`flex-1 pt-3 sm:pt-3 lg:pt-4 pb-1 sm:pb-1 lg:pb-2 ${
          shouldEnableSidebarScroll ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden'
        }`}
      >
        {/* Inline Workspace Creation */}
        {isCreatingWorkspace && (
          <div className="px-3 lg:px-5 py-1.5 lg:py-2 bg-gray-50 dark:bg-neutral-800 rounded-md border border-gray-200 dark:border-neutral-600 mb-1.5 lg:mb-2 mx-2 lg:mx-3">
            <div className="space-y-1.5 lg:space-y-2">
              <input
                type="text"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Enter workspace name"
                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-neutral-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-gray-100"
                autoFocus
              />
              <div className="flex space-x-2">
                <button
                  onClick={handleSubmitCreate}
                  disabled={!newWorkspaceName.trim() || isCreating}
                  className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
                >
                  {isCreating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    'Create'
                  )}
                </button>
                <button
                  onClick={handleCancelCreate}
                  className="px-2 py-1 bg-gray-300 dark:bg-neutral-600 text-gray-700 dark:text-neutral-300 text-xs rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Current Workspace Menu Items */}
        {currentWorkspace && (
          <div className="mb-1 lg:mb-2 min-h-full flex flex-col">
            <div className="space-y-0.5 lg:space-y-1">
              {workspaceMenuItems.map((item, index) => {
                // Campaigns is now a regular item, so we removed the special handling block


                // Contacts — plain nav link (sub-navigation moved to horizontal bar inside the page)
                if (item.name === 'Contacts') {
                  const contactsPath = resolveWorkspacePath(item.path);
                  const isContactsActive =
                    isActiveRoute(`/workspace/${currentWorkspace.id}${item.path}`) ||
                    location.pathname.includes('/contacts/');
                  return (
                    <button
                      key={item.path}
                      id="walkthrough-contacts-tab"
                      onClick={() => {
                        handleNavigation(contactsPath);
                        setIsSettingsExpanded(false);
                        setIsShopifyExpanded(false);
                        setIsContactsExpanded(false);
                      }}
                      className={`group relative w-[calc(100%-20px)] mx-[10px] rounded-md flex items-center py-2.5 sm:py-2 lg:py-2 pl-3 lg:pl-3.5 text-left transition-colors duration-200 touch-manipulation ${
                        isContactsActive ? navActiveClass : navInactiveClass
                      }`}
                    >
                      <span className="ml-2 lg:ml-3 mr-1.5 lg:mr-2 flex-shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:scale-110">
                        {item.icon}
                      </span>
                      <span className="text-[14px] sm:text-[15px] lg:text-[13px] font-[300] flex-1">{item.name}</span>
                    </button>
                  );
                }

                // Regular menu items
                const targetPath = resolveWorkspacePath(item.path);
                const isInsightsRoute = item.name === 'Insights' && (
                  location.pathname.includes('/analytics') || location.pathname.includes('/reports')
                );
                const isActive = isInsightsRoute || isActiveRoute(targetPath);
                const isDashboard = item.name === 'Dashboard';
                const isInsights = item.name === 'Insights';
                const isWorkspaceSettings = item.name === 'Workspace settings';
                const showLock = isCurrentWorkspaceLocked && !isDashboard && !isInsights && !isWorkspaceSettings;

                return (
                  <button
                    key={item.path}
                    id={
                      isDashboard ? 'walkthrough-dashboard-tab' :
                        item.name === 'Campaigns' ? 'walkthrough-campaigns-tab' :
                          item.name === 'Insights' ? 'walkthrough-insights-tab' :
                            undefined
                    }
                    onClick={(e) => {
                      if (showLock) {
                        e.stopPropagation();
                        setActiveLockTooltip(activeLockTooltip === item.path ? null : item.path);
                        return;
                      }
                      handleNavigation(targetPath);
                      setIsSettingsExpanded(false);
                      setIsContactsExpanded(false);
                      setIsShopifyExpanded(false);

                    }}
                    className={`group relative w-[calc(100%-20px)] mx-[10px] rounded-md flex items-center py-2.5 sm:py-2 lg:py-2 pl-3 lg:pl-3.5 text-left transition-colors duration-200 touch-manipulation ${isActive
                      ? navActiveClass
                      : navInactiveClass
                      } ${showLock ? 'opacity-70' : ''}`}
                  >
                    <span className="ml-2 lg:ml-3 mr-1.5 lg:mr-2 flex-shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:scale-110">
                      {item.icon}
                    </span>
                    <span className="text-[14px] sm:text-[15px] lg:text-[13px] font-[300] flex-1">{item.name}</span>
                    {item.badge && <span className="mr-2 lg:mr-3 text-[9px] lg:text-[10px] font-semibold uppercase tracking-wider text-[#FADC87]">{item.badge}</span>}
                    {showLock && (
                      <div className="flex items-center">
                        <svg className={`w-4 h-4 mr-4 flex-shrink-0 text-white`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        {activeLockTooltip === item.path && (
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 mr-14 z-[100] animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                            <div className="bg-indigo-600 dark:bg-indigo-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-xl whitespace-nowrap">
                              Upgrade to Pro
                            </div>
                            <div className="absolute right-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-indigo-600 dark:bg-indigo-500 rotate-45"></div>
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Separator Line after Workspace Settings */}
            <div className="px-2 lg:px-3 py-2 lg:py-4">
              <div className={`border-t ${sidebarBorderClass}`}></div>
            </div>

            {/* Your Content Section */}
            <div className="space-y-0.5 lg:space-y-1">
              <button
                onClick={() => {
                  handleNavigation('/content-manager');
                  setIsSettingsExpanded(false);
                  setIsContactsExpanded(false);
                  setIsShopifyExpanded(false);
                }}
                className={`group w-[calc(100%-20px)] mx-[10px] rounded-md flex items-center py-2.5 sm:py-2 lg:py-2 pl-3 lg:pl-3.5 text-left transition-colors duration-200 touch-manipulation ${location.pathname === '/content-manager'
                  ? navActiveClass
                  : navInactiveClass
                  }`}
              >
                <span className="ml-2 lg:ml-3 mr-1.5 lg:mr-2 flex-shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:scale-110">
                  <FolderOpen className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-4 lg:h-4" strokeWidth={1.8} />
                </span>
                <span className="text-[14px] sm:text-[15px] lg:text-[13px] font-[300] flex-1">Your Content</span>
              </button>
            </div>

            {/* Templates Shortcut */}
            <div className="space-y-0.5 lg:space-y-1">
              <button
                onClick={() => {
                  handleNavigation(templatesPath);
                  setIsSettingsExpanded(false);
                  setIsContactsExpanded(false);
                  setIsShopifyExpanded(false);

                }}
                className={`group w-[calc(100%-20px)] mx-[10px] rounded-md flex items-center py-2.5 sm:py-2 lg:py-2 pl-3 lg:pl-3.5 text-left transition-colors duration-200 touch-manipulation ${isActiveRoute(templatesPath)
                  ? navActiveClass
                  : navInactiveClass
                  }`}
              >
                <span className="ml-2 lg:ml-3 mr-1.5 lg:mr-2 flex-shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:scale-110">
                  <LayoutTemplate className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-4 lg:h-4" strokeWidth={1.8} />
                </span>
                <span className="text-[14px] sm:text-[15px] lg:text-[13px] font-[300]">Templates</span>
              </button>
            </div>

            {/* Settings Dropdown */}
            <div className="space-y-0.5 lg:space-y-1">
              <button
                onClick={() => {
                  setIsSettingsExpanded(!isSettingsExpanded);
                  setIsContactsExpanded(false);
                  setIsShopifyExpanded(false);

                }}
                className={`group w-[calc(100%-20px)] mx-[10px] rounded-md flex items-center py-2.5 sm:py-2 lg:py-2 pl-3 lg:pl-3.5 text-left transition-colors duration-200 touch-manipulation ${isSettingsExpanded
                  ? navActiveClass
                  : navInactiveClass
                  }`}
              >
                <span className="ml-2 lg:ml-3 mr-1.5 lg:mr-2 flex-shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:scale-110">
                  <Settings className="w-3.5 h-3.5 lg:w-4 lg:h-4" strokeWidth={1.8} />
                </span>
                <span className="text-[14px] sm:text-[15px] lg:text-[13px] font-[300] flex-1">Settings</span>
                <svg
                  className={`w-3.5 h-3.5 lg:w-4 lg:h-4 mr-2 lg:mr-3 transition-transform duration-200 ${isSettingsExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Settings Submenu */}
              <div
                className={`relative ml-4 lg:ml-6 mt-0 space-y-0 overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isSettingsExpanded ? 'max-h-64 opacity-100 translate-y-0' : 'max-h-0 opacity-0 -translate-y-3 pointer-events-none'}`}
                aria-hidden={!isSettingsExpanded}
              >
                  <span
                    className={`pointer-events-none absolute left-5 top-0.5 bottom-0.5 w-px bg-white/20`}
                    aria-hidden="true"
                  />
                  {settingsSubmenuItems.map((subItem, index) => {
                    const fullPath = currentWorkspace
                      ? `/workspace/${currentWorkspace.id}${subItem.path}`
                      : subItem.path;
                    const isActive = location.pathname === subItem.path ||
                      location.pathname === fullPath ||
                      location.pathname.startsWith(subItem.path);

                    return (
                      <button
                        key={subItem.path}
                        onClick={() => {
                          handleNavigation(subItem.path);
                          setIsContactsExpanded(false);
                          setIsShopifyExpanded(false);
                          // Keep Settings dropdown open when clicking its items
                        }}
                        className={`relative w-[calc(100%-20px)] mx-[10px] flex items-center py-1 sm:py-1 lg:py-1 text-left transition-all duration-300 touch-manipulation ${isSettingsExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}
                        style={{ transitionDelay: isSettingsExpanded ? `${index * 45}ms` : '0ms' }}
                      >
                        <span
                          className={`pointer-events-none absolute left-5 top-1/2 h-px w-2.5 -translate-y-1/2 bg-white/25`}
                          aria-hidden="true"
                        />
                        <span className={`ml-8 lg:ml-9 inline-flex w-[calc(100%-2.25rem)] rounded-md px-2.5 py-0.5 ${isActive ? submenuActiveClass : submenuInactiveClass}`}>
                          <span className="text-[13px] sm:text-[14px] lg:text-[13px] font-[300]">{subItem.name}</span>
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Shopify Section (extreme bottom, separate from main nav) */}
            {isShopifyConnected && (
              <div className="mt-auto pt-0 pb-3 lg:pb-4">
                <div className="relative space-y-0.5 lg:space-y-1">
                  <button
                    ref={shopifyButtonRef}
                    onClick={() => {
                      if (!isShopifyExpanded) {
                        updateShopifyFlyoutPosition();
                      }
                      setIsShopifyExpanded(!isShopifyExpanded);
                      setIsContactsExpanded(false);
                      setIsSettingsExpanded(false);
                    }}
                    className={`w-full flex items-center py-1.5 sm:py-1.5 lg:py-1.5 pl-2.5 pr-2 text-left transition-colors duration-200 touch-manipulation ${
                      isShopifyExpanded || isShopifySectionActive
                        ? 'text-white'
                        : 'text-white/75 hover:text-white'
                    }`}
                  >
                    <span className="ml-1.5 lg:ml-2 mr-1.5 flex-shrink-0">
                      <span className="w-4 h-4 flex items-center justify-center overflow-hidden">
                        <img
                          loading="lazy"
                          src="/images/shopify-bag.svg"
                          alt="Shopify"
                          className="w-4 h-4 object-contain opacity-90"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = 'none';
                          }}
                        />
                      </span>
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13px] font-medium tracking-[0.01em] leading-tight">Shopify Store</span>
                      {shopifyStoreName && (
                        <span className={`block text-[10px] truncate leading-tight mt-0 text-white/60`}>
                          {shopifyStoreName}
                        </span>
                      )}
                    </span>
                    <svg
                      className={`w-4 h-4 mr-2 transition-transform duration-200 opacity-90 ${isShopifyExpanded ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {isShopifyExpanded && typeof document !== 'undefined' && createPortal(
                    <div
                      ref={shopifyMenuRef}
                      className="fixed w-72 rounded-2xl border border-slate-200/90 bg-white/95 backdrop-blur-md shadow-[0_24px_60px_rgba(2,12,27,0.24)] z-[99999] p-2"
                      style={{ top: `${shopifyMenuPosition.top}px`, left: `${shopifyMenuPosition.left}px` }}
                    >
                      <div className="rounded-xl bg-slate-50/80 p-2.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">Shopify Store</p>
                            <p className="mt-1 truncate text-[13px] font-semibold text-slate-900">{shopifyStoreName || 'Connected store'}</p>
                            {shopifyStoreDomain && (
                              <div className="mt-1 flex items-center gap-2 text-[10px]">
                                <a
                                  href={`https://${shopifyStoreDomain}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-slate-600 hover:text-slate-900 hover:underline"
                                >
                                  Open store
                                </a>
                                <span className="text-slate-300">•</span>
                                <a
                                  href={`https://${shopifyStoreDomain}/admin`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-slate-600 hover:text-slate-900 hover:underline"
                                >
                                  Shopify admin
                                </a>
                              </div>
                            )}
                          </div>
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-semibold text-emerald-700">
                            Connected
                          </span>
                        </div>

                        <div className="mt-2.5 flex items-center justify-between rounded-lg bg-white/90 px-2 py-1.5">
                          <div className="min-w-0">
                            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">Last sync</p>
                            <p className="truncate text-[11px] text-slate-700">
                              {isShopifySyncing ? 'Syncing now...' : formatRelativeSyncTime(shopifyLastSyncAt)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleShopifySync}
                            disabled={isShopifySyncing}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                            title="Sync now"
                            aria-label="Sync Shopify data"
                          >
                            <svg
                              className={`h-4 w-4 ${isShopifySyncing ? 'animate-spin' : ''}`}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M20 9a8 8 0 00-13.66-4.66L4 6M4 15a8 8 0 0013.66 4.66L20 18" />
                            </svg>
                          </button>
                        </div>

                        <div className="mt-2.5 grid grid-cols-3 gap-1.5">
                          {[
                            { label: 'Customers', value: shopifyStats.customers },
                            { label: 'Orders', value: shopifyStats.orders },
                            { label: 'Products', value: shopifyStats.products },
                          ].map((item) => (
                            <div key={item.label} className="rounded-lg bg-white/90 px-2 py-1.5">
                              <p className="text-[9px] uppercase tracking-wide text-slate-400">{item.label}</p>
                              <p className="mt-0.5 text-[12px] font-semibold text-slate-900">
                                {isShopifyStatsLoading ? '...' : item.value.toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>

                      </div>

                      <div className="mt-2 rounded-xl bg-white/80 py-1">
                        {shopifySubmenuItems.map((subItem) => (
                          <button
                            key={subItem.path}
                            onClick={() => {
                              handleNavigation(subItem.path);
                              setIsContactsExpanded(false);
                              setIsSettingsExpanded(false);
                              setIsShopifyExpanded(false);
                            }}
                            className={`w-full flex items-center py-1.5 px-2.5 text-left transition-all duration-150 touch-manipulation text-[12px] ${
                              isActiveRoute(subItem.path)
                                ? 'bg-gradient-to-r from-[#F3F4F6] to-[#E5E7EB] text-slate-800'
                                : 'text-slate-700 hover:bg-slate-100/90 hover:text-slate-900'
                            }`}
                          >
                            <span className="font-medium leading-5">{subItem.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};

export default Sidebar;

// Ensure this file is recognized as a module
export { };
